<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white"> Tournées technicien </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <TableHeaderButtons
            :attr="toolTipText"
            @add="add"
            @remove="remove"
            @reload="reloadData"
            @export="exportTable"
            @search="updateSearchPanel"
            :hasSelectedLines="selectedTaskListIds.length > 0"
          />
        </div>
      </v-toolbar>
      <v-row class="px-4 py-2" align="center" density="compact">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-4">
            <v-switch
              v-model="todo"
              label="ÀFaire"
              color="error"
              density="compact"
              inset
              hide-details
            />
            <v-switch
              v-model="assigned"
              label="Assigné"
              color="warning"
              density="compact"
              inset
              hide-details
            />
            <v-switch
              v-model="inProgress"
              label="EnCours"
              color="primary"
              density="compact"
              inset
              hide-details
            />
            <v-switch
              v-model="faite"
              label="Faite"
              color="#ffbbee"
              density="compact"
              inset
              hide-details
            />
            <v-switch
              v-model="approved"
              label="Approuvé"
              color="success"
              density="compact"
              inset
              hide-details
            />
          </div>
        </v-col>
      </v-row>
      <Table
        :loading="loading"
        :searchField="true"
        :columns="columns"
        :items="taskLists"
        :showSelect="true"
        storageKey="taskListTablePaginationState"
        @edit="edit"
        @rowClicked="rowClicked"
        @selectionChanged="selectionChanged"
      />
    </v-card>

    <v-overlay :model-value="loading">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!-- Search panel -->
    <SearchPanel
      :rightDrawer="rightDrawer"
      @cancelSearch="cancelSearch"
      @searchData="searchTaskLists"
    >
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Nom" v-model="searchFilter.contain.name" />
        </v-col>

        <v-col cols="11" offset="1">
          <v-text-field label="Signataire" v-model="searchFilter.contain.signataire" />
        </v-col>

        <v-col cols="11" offset="1">
          <v-text-field label="Telephone" v-model="searchFilter.contain.telephone" />
        </v-col>

        <v-col cols="11" offset="1">
          <v-text-field label="Courriel" v-model="searchFilter.contain.email" />
        </v-col>
      </v-row>
    </SearchPanel>

    <ConfirmDialog
      v-model="dialog"
      :dialogTitle="dialogTitle"
      :dialogText="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
      confirm-button-text="Confirmer"
      cancel-button-text="Annuler"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";

import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

import {
  buildSearchFilters,
  buildJsonServerQuery,
  clearSearchFilters,
} from "@/utils/app-util";

import { useAppStore } from "@/store/app";
import { TableColumn, TaskListStatus } from "@/types";
import { TaskList, getTaskListExport } from "@/types";
import { createAndSaveDocument } from "@/utils/docx-util";
import {
  deleteTaskList,
  searchForTaskLists,
  getFilteredTaskLists,
  getTaskListById,
} from "@/utils/tasklist-api";

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false);
const dialogTitle = "Suppression";
const dialogText =
  "Voulez-vous supprimer les tournées selectionnées?\nLes activités rattachées seront également supprimées.";

const showSearchPanel = ref(false);
const loading = ref(false);

const todo = ref(true);
const assigned = ref(false);
const inProgress = ref(false);
const faite = ref(false);
const approved = ref(false);

const taskLists = ref<TaskList[]>([]);
const selectedTaskListIds = ref<string[]>([]);

const columns: TableColumn[] = [
  { title: "Code tournée", key: "codeTournee", align: "start" },
  { title: "Début planifié", key: "startDate" },
  { title: "Fin planifié", key: "endDate" },
  { title: "Techniciens", key: "technicianNames" },
  { title: "Status", key: "taskListStatus" },
  { title: "", key: "actions", sortable: false },
];

const searchFilter = reactive({
  contain: {
    name: "",
    signataire: "",
    telephone: "",
    email: "",
  },
});

let query = "";

const toolTipText = computed(() => "une tournée");
const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: (v: boolean) => (showSearchPanel.value = v),
});

watch([todo, assigned, inProgress, , faite, approved], () => {
  saveFiltersToLocalStorage();
  fetchTaskLists();
});

function selectionChanged(selected: string[]) {
  selectedTaskListIds.value = [...selected];
}

function rowClicked(item: TaskList) {
  router.push(`tasklists/${item.id}`);
}

function edit(item: TaskList) {
  router.push(`tasklists/${item.id}`);
}

function add() {
  router.push("NewTaskList");
}

function remove() {
  dialog.value = true;
}

async function onConfirm() {
  dialog.value = false;
  try {
    loading.value = true;
    await deleteTaskList(selectedTaskListIds.value);
    await fetchTaskLists();
    selectedTaskListIds.value = [];
    appStore.sendSuccessNotice("TaskList has been deleted.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function onCancel() {
  dialog.value = false;
}

async function searchTaskLists() {
  showSearchPanel.value = false;
  buildSearchFilters(searchFilter);
  query = buildJsonServerQuery(searchFilter);
  await doSearchTaskLists(query);
}

async function doSearchTaskLists(searchQuery: string) {
  try {
    loading.value = true;
    taskLists.value = await searchForTaskLists(`tasklists?${searchQuery}`);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function reloadData() {
  query = "";
  fetchTaskLists();
}

const exportTable = async () => {
  if (loading.value) {
    return;
  }

  const taskListIds = [...selectedTaskListIds.value];
  if (!taskListIds.length) {
    return;
  }

  try {
    loading.value = true;

    const selectedTaskLists = await Promise.all(
      taskListIds.map((id) => getTaskListById(id))
    );

    createAndSaveDocument(
      "export_tournees_techniciens",
      "../templates/ExportTourneesTemplate.docx",
      getTaskListExport(selectedTaskLists)
    );
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

function updateSearchPanel() {
  rightDrawer.value = !rightDrawer.value;
}

function cancelSearch() {
  showSearchPanel.value = false;
}

function saveFiltersToLocalStorage() {
  localStorage.setItem(
    "taskListTableFilters",
    JSON.stringify({
      todo: todo.value,
      assigned: assigned.value,
      inProgress: inProgress.value,
      faite: faite.value,
      approved: approved.value,
    })
  );
}

function loadFiltersFromLocalStorage() {
  const raw = localStorage.getItem("taskListTableFilters");
  if (!raw) return;
  try {
    const f = JSON.parse(raw);
    todo.value = !!f.todo;
    assigned.value = !!f.assigned;
    inProgress.value = !!f.inProgress;
    faite.value = !!f.faite;
    approved.value = !!f.approved;
  } catch {
    resetFilters();
  }
}

function resetFilters() {
  todo.value = true;
  assigned.value = false;
  inProgress.value = false;
  faite.value = false;
  approved.value = false;
}

async function fetchTaskLists() {
  try {
    loading.value = true;

    const statuses: TaskListStatus[] = [];
    if (todo.value) statuses.push(TaskListStatus.A_FAIRE);
    if (assigned.value) statuses.push(TaskListStatus.ASSIGNE);
    if (inProgress.value) statuses.push(TaskListStatus.EN_COURS);
    if (faite.value) statuses.push(TaskListStatus.FAITE);
    if (approved.value) statuses.push(TaskListStatus.APPROUVE);

    if (!statuses.length) {
      taskLists.value = [];
      return;
    }

    const response = await getFilteredTaskLists(statuses);
    taskLists.value = response.sort(
      (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadFiltersFromLocalStorage();
  fetchTaskLists();
});
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
